import { ReactNode, useCallback } from 'react';
import { Thead, Tr, Th as CKTh, TableColumnHeaderProps } from '@chakra-ui/react';
import { Colors } from '@mercator-gcp/grout';
import SortableHeader from 'components/layout/Table/SortableHeader';
import { useDocumentTableHeaderSorting } from 'state/filterDocuments';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';

const tableHeader = {
  fontWeight: '600',
  fontSize: '10px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'brand.dark-green',
};

const stickyHeaderStyle = {
  '>tr>th': {
    position: 'sticky',
    top: '0',
    backgroundColor: Colors.White,
    zIndex: Z_INDEX_DICT.LEAD_TABLE_HEADER,
  },
};

const centerAlignStyle = { textAlign: 'center' };

const COLUMN_WIDTH = {
  DATE_CREATED: '120px',
  LAST_UPDATED: '120px',
  DOCUMENT_TYPE: '200px',
  ADDRESS: '200px',
  DESCRIPTION: '300px',
  PROJECT_VALUE: '132px',
  COMPANIES_INVOLVED: '220px',
  PROJECT_LINK: '156px',
};

type ThProps = {
  colWidth?: string;
  children: ReactNode;
  sx?: TableColumnHeaderProps['sx'];
};

const Th = ({ colWidth = 'auto', sx = {}, children }: ThProps) => {
  return (
    <CKTh width={colWidth} sx={{ ...tableHeader, ...sx }}>
      {children}
    </CKTh>
  );
};

const TABLE_HEADINGS = {
  DATE_CREATED: {
    key: 'earliest_activity_date',
    label: 'Date Created',
  },
  LAST_UPDATED: {
    key: 'latest_activity_date',
    label: 'Last Updated',
  },
  STATUS: {
    key: 'status',
    label: 'Status',
  },
  DOCUMENT_TYPE: {
    key: 'documentType',
    label: 'Document Type',
  },
  ADDRESS: {
    key: 'address',
    label: 'Address',
  },
  DESCRIPTION: {
    key: 'description',
    label: 'Description',
  },
  PROJECT_VALUE: {
    key: 'estimated_project_cost',
    label: 'Project Value',
  },
  COMPANIES_INVOLVED: {
    key: 'companiesInvolved',
    label: 'Companies Involved',
  },
  PROJECT_LINK: {
    key: 'projectLink',
    label: 'Project Link',
  },
};

const TableHeader = () => {
  const { isSortDescending, sortedHeader, toggleSortedHeader } =
    useDocumentTableHeaderSorting();

  const handleHeaderClick = useCallback(
    (headingKey: string) => {
      toggleSortedHeader(headingKey);
    },
    [sortedHeader, isSortDescending, toggleSortedHeader],
  );

  return (
    <Thead sx={stickyHeaderStyle}>
      <Tr>
        <Th colWidth={COLUMN_WIDTH.DATE_CREATED}>
          <SortableHeader
            onClick={() => {
              handleHeaderClick(TABLE_HEADINGS.DATE_CREATED.key);
            }}
            headingKey={TABLE_HEADINGS.DATE_CREATED.key}
            currentHeading={sortedHeader}
            isSorted={isSortDescending}
            label={TABLE_HEADINGS.DATE_CREATED.label}
          />
        </Th>
        <Th colWidth={COLUMN_WIDTH.LAST_UPDATED}>
          <SortableHeader
            onClick={() => {
              handleHeaderClick(TABLE_HEADINGS.LAST_UPDATED.key);
            }}
            headingKey={TABLE_HEADINGS.LAST_UPDATED.key}
            currentHeading={sortedHeader}
            isSorted={isSortDescending}
            label={TABLE_HEADINGS.LAST_UPDATED.label}
          />
        </Th>
        <Th colWidth={COLUMN_WIDTH.DOCUMENT_TYPE}>
          {TABLE_HEADINGS.DOCUMENT_TYPE.label}
        </Th>
        <Th colWidth={COLUMN_WIDTH.ADDRESS}>{TABLE_HEADINGS.ADDRESS.label}</Th>
        <Th colWidth={COLUMN_WIDTH.DESCRIPTION}>{TABLE_HEADINGS.DESCRIPTION.label}</Th>
        <Th colWidth={COLUMN_WIDTH.PROJECT_VALUE}>
          <SortableHeader
            onClick={() => {
              handleHeaderClick(TABLE_HEADINGS.PROJECT_VALUE.key);
            }}
            headingKey={TABLE_HEADINGS.PROJECT_VALUE.key}
            currentHeading={sortedHeader}
            isSorted={isSortDescending}
            label={TABLE_HEADINGS.PROJECT_VALUE.label}
          />
        </Th>
        <Th colWidth={COLUMN_WIDTH.COMPANIES_INVOLVED}>
          {TABLE_HEADINGS.COMPANIES_INVOLVED.label}
        </Th>
        <Th colWidth={COLUMN_WIDTH.PROJECT_LINK} sx={centerAlignStyle}>
          {TABLE_HEADINGS.PROJECT_LINK.label}
        </Th>
      </Tr>
    </Thead>
  );
};

export default TableHeader;
